import React, {
  FC,
  memo,
  useCallback,
  useMemo,
}                                     from 'react';
import {
  Formik,
  FormikConfig,
  useFormikContext,
}                                     from 'formik';
import { CircularProgress }           from '@material-ui/core';
import { IFeeValues }                 from '@typings/accountManagement';
import { IBulkPricingList, IPricing } from '@typings/invoiceManagement';
import {
  formatFeeValue,
  getValuesFromCheckboxOptions,
}                                     from '@utils/commonFunctions';
import { EFullFeeType }               from '@enums/invoiceManagement';
import { ICheckboxOption }            from '@components/Checkbox/CheckboxFieldGroup';
import { ModalDialog }                from '@components/index';
import { BulkAccountsForm }           from './BulkAccountsForm';
import { PRICING_LABELS }             from '../utils/suggestions';
import { bulkValidationSchema }       from '../utils/validation';

interface IProps {
  isOpen               : boolean;
  isLoading            : boolean;
  organizationsOptions : ICheckboxOption[];
  handleUpdateBulkFees : (feesValues: IFeeValues[], accountList: IBulkPricingList) => void;
  toggleModal          : () => void;
}

const BulkAccountsModalBase: FC<IProps> = ({
  isOpen,
  isLoading,
  organizationsOptions,
  handleUpdateBulkFees,
  toggleModal,
}) => {
  const { values: feeValues } = useFormikContext<IFeeValues[]>();

  const defaultPricing = useMemo(
    () => feeValues.map((item: IFeeValues): ICheckboxOption => {
      const { type, userType, value, taxable } = item;

      return {
        value : true,
        name  : `${type}-${userType}`,
        label : `${PRICING_LABELS[`${EFullFeeType[userType === EFullFeeType.personalFee
          ? EFullFeeType.personalFee
          : type - 1]}` as keyof IPricing]} (${formatFeeValue(value)}, ${taxable ? 'taxable' : 'non-taxable'})`,
      };
    }),
    [feeValues],
  );

  const initialValues = useMemo(
    () => ({
      accounts : getValuesFromCheckboxOptions(organizationsOptions),
      fees     : getValuesFromCheckboxOptions(defaultPricing),
    }),
    [defaultPricing, organizationsOptions],
  );

  const onSubmit = useCallback<FormikConfig<IBulkPricingList>['onSubmit']>(
    (values, { resetForm }) => {
      handleUpdateBulkFees(feeValues, values);
      resetForm();
      toggleModal();
    },
    [feeValues, handleUpdateBulkFees, toggleModal],
  );

  return (
    <ModalDialog
      open     = {isOpen}
      actions  = {null}
      width    = "700px"
      maxwidth = "1000px"
      padding  = "0 30px"
      content  = {
        isLoading ? (
          <CircularProgress color="secondary" />
        ) : (
          <Formik
            initialValues    = {initialValues}
            validationSchema = {bulkValidationSchema}
            onSubmit         = {onSubmit}
          >
            <BulkAccountsForm
              feesValues           = {feeValues}
              defaultPricing       = {defaultPricing}
              organizationsOptions = {organizationsOptions}
              handleUpdateBulkFees = {handleUpdateBulkFees}
              toggleModal          = {toggleModal}
            />
          </Formik>
        )
      }
    />
  );
};

export const BulkAccountsModal = memo(BulkAccountsModalBase);
